/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import { NumericFormat } from 'react-number-format';
import EnvelopeWithBox from '../EnvelopeWithBox';
import Styles from '../../css/LoanInformation.module.css';

import { useLoanInformationContext } from '../../contexts/LoanInformationContext';
import {
  addMonthsToDate,
  addMonthsYrsToTimestamp,
  epochToDate,
  formatDate,
  getTimestampAsDate,
} from '../../common/UtilityFunctions';
import useBillingCycle from '../../common/hooks/useBillingCycle';
import { useCallback, useState } from 'react';
import { useViewingContext } from '../../contexts/ViewingContext';
import { useAuthContext } from '../../contexts/AuthContext';
import PopoverInfo from '../PopoverInfo';

const LoanHeloc = () => {
  const {
    lnptData,
    servicingData,
    loanData,
    annualFeeData,
    loanEvents,
    drawEndSeconds,
    repayEndSeconds,
    repayStartSeconds,
    lnptPayData,
    handleAnnualFeeChange,
  } = useLoanInformationContext();
  const { billingCycleData } = useBillingCycle();
  const { isServicerView } = useViewingContext();
  const { helocEditAccess, servicerUser } = useAuthContext();

  const canEditHeloc = servicerUser && helocEditAccess;

  const getBillingCycleDate = useCallback(() => {
    if (isServicerView) {
      return billingCycleData?.value ? billingCycleData?.value : 16;
    } else {
      return lnptPayData?.cutOffDate ? getTimestampAsDate(lnptPayData?.cutOffDate) : '';
    }
  }, [billingCycleData, lnptPayData, isServicerView]);
  return (
    <EnvelopeWithBox blockName='HELOC Details'>
      <div className={Styles.loanInfoTable}>
        <div className={Styles.leftColumn}>
          <table className={Styles.loanInfoTableSection}>
            <tbody>
              <tr>
                <th>
                  {'Initial Advance'}
                  <PopoverInfo text='The initial draw amount at the time of closing.'></PopoverInfo>
                </th>
                <td>
                  <NumericFormat
                    displayType='text'
                    value={loanData?.creditFunded ? loanData?.creditFunded : loanData?.defaultCreditFunded}
                    decimalScale={2}
                    fixedDecimalScale
                    prefix='$'
                    thousandSeparator
                  />
                </td>
              </tr>
              {isServicerView && (
                <tr>
                  <th>Financed Closing Costs</th>
                  <td>
                    <NumericFormat
                      displayType='text'
                      value={
                        loanData?.estimatedClosingCosts +
                        loanData?.prePaidInterest -
                        (loanData?.directlyPaidAmount - loanData?.closingCostsPaidInAdvance)
                      }
                      decimalScale={2}
                      fixedDecimalScale
                      prefix='$'
                      thousandSeparator
                    />
                  </td>
                </tr>
              )}
              <tr>
                <th>{'Available Funds to Draw'}</th>
                <td>
                  <NumericFormat
                    displayType='text'
                    value={loanData?.loanAmount - lnptData?.upb}
                    decimalScale={2}
                    fixedDecimalScale
                    prefix='$'
                    thousandSeparator
                  />
                </td>
              </tr>
              <tr>
                <th>Early Termination Fee</th>
                <td>
                  <NumericFormat
                    displayType='text'
                    value={loanData?.earlyTerminationFee ? loanData?.earlyTerminationFee : 0}
                    decimalScale={2}
                    fixedDecimalScale
                    prefix='$'
                    thousandSeparator
                  />
                </td>
              </tr>
              {isServicerView && (
                <tr>
                  <th>Early Termination Period (months)</th>
                  <td>
                    <NumericFormat
                      displayType='text'
                      value={servicingData?.earlyTerminationPeriod ? servicingData?.earlyTerminationPeriod : 0}
                      decimalScale={0}
                    />
                  </td>
                </tr>
              )}
              <tr>
                <th>
                  {'Early Termination End Date'}

                  <PopoverInfo text='An early termination fee will apply if the HELOC loan is paid off before this date.'></PopoverInfo>
                </th>
                <td>
                  {loanEvents?.SDT?.activeDate
                    ? getTimestampAsDate(
                        addMonthsYrsToTimestamp(
                          loanEvents?.SDT?.activeDate,
                          servicingData?.earlyTerminationPeriod ? servicingData?.earlyTerminationPeriod : 0,
                          0,
                        ),
                      )
                    : ''}
                </td>
              </tr>
              <tr>
                <th>Minimum Draw Amount</th>
                <td>
                  <NumericFormat
                    displayType='text'
                    value={servicingData?.minimumAdvance ? servicingData?.minimumAdvance : 0.0}
                    decimalScale={2}
                    fixedDecimalScale
                    prefix='$'
                    thousandSeparator
                  />
                </td>
              </tr>
              {isServicerView && (
                <tr>
                  <th>Minimum Balance</th>
                  <td>
                    <NumericFormat
                      displayType='text'
                      value={servicingData?.minimumBalance ? servicingData?.minimumBalance : 0.0}
                      decimalScale={2}
                      fixedDecimalScale
                      prefix='$'
                      thousandSeparator
                    />
                  </td>
                </tr>
              )}
              {isServicerView && (
                <tr>
                  <th>I/R Life Cap</th>
                  <td>
                    <NumericFormat
                      displayType='text'
                      value={loanData?.interestLifeCap ? loanData?.interestLifeCap : 0}
                      suffix='%'
                      thousandSeparator
                    />
                  </td>
                </tr>
              )}
              {isServicerView && (
                <tr>
                  <th>I/R Life Min</th>
                  <td>
                    <NumericFormat
                      displayType='text'
                      value={loanData?.lifeFloor ? loanData?.lifeFloor : 0}
                      suffix='%'
                      thousandSeparator
                    />
                  </td>
                </tr>
              )}
              {isServicerView && (
                <tr>
                  <th>I/R Margin</th>
                  <td>
                    <NumericFormat
                      displayType='text'
                      value={loanData?.margin ? loanData?.margin : 0}
                      suffix='%'
                      thousandSeparator
                    />
                  </td>
                </tr>
              )}
            </tbody>
            {isServicerView && (
              <tr>
                <td colSpan={2} style={{ height: '20px' }}></td>
              </tr>
            )}
          </table>
        </div>
        <div className={Styles.rightColumn}>
          <table className={Styles.loanInfoTableSection}>
            <tbody>
              <tr>
                <th>Draw Start Date</th>
                <td>{getTimestampAsDate(loanEvents?.SDT?.activeDate)}</td>
              </tr>
              {isServicerView && (
                <tr>
                  <th>Draw Period (months): </th>
                  <td>
                    <NumericFormat
                      displayType='text'
                      value={loanData?.interestOnlyPeriod ? loanData?.interestOnlyPeriod : ''}
                      decimalScale={0}
                    />
                  </td>
                </tr>
              )}
              <tr>
                <th>Draw End Date</th>
                <td>{drawEndSeconds != null ? getTimestampAsDate(drawEndSeconds) : ''}</td>
              </tr>
              <tr>
                <th>Payback Period Start Date</th>
                <td>{repayStartSeconds != null ? getTimestampAsDate(repayStartSeconds) : ''}</td>
              </tr>
              {isServicerView && (
                <tr>
                  <th>Payback Period (months)</th>
                  <td>{loanData?.loanTerm - loanData?.interestOnlyPeriod}</td>
                </tr>
              )}
              <tr>
                <th>
                  {'Payback Period End Date'}
                  <PopoverInfo text='This is the end date of the repayment period, which is the same as the maturity date of the loan.The remaining balance on the loan must be paid by the borrower by this date.'></PopoverInfo>
                </th>
                <td>{repayEndSeconds != null ? getTimestampAsDate(repayEndSeconds) : ''}</td>
              </tr>
              {isServicerView && (
                <tr>
                  <th>Prime Start Date</th>
                  <td>{getTimestampAsDate(loanEvents?.PRI?.activeDate)}</td>
                </tr>
              )}
              {isServicerView && (
                <tr>
                  <th>Billing Cycle Day</th>
                  <td>{getBillingCycleDate()}</td>
                </tr>
              )}
              <tr>
                <th>Annual Fee</th>
                <td>
                  <NumericFormat
                    displayType={canEditHeloc ? 'input' : 'text'}
                    disabled={!canEditHeloc}
                    value={annualFeeData?.dollarAmount ? annualFeeData?.dollarAmount : 0}
                    decimalScale={2}
                    fixedDecimalScale
                    prefix='$'
                    thousandSeparator
                    onValueChange={(values) => {
                      handleAnnualFeeChange('edit', 'dollarAmount', values.floatValue);
                      handleAnnualFeeChange('edit', 'active', true);
                    }}
                  />
                </td>
              </tr>
            </tbody>
            {isServicerView && (
              <tr>
                <td colSpan={2} style={{ height: '20px' }}></td>
              </tr>
            )}
          </table>
        </div>
      </div>
    </EnvelopeWithBox>
  );
};

export default LoanHeloc;
